import React, { useState, useEffect } from 'react';
import {
  ChartBarIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ClockIcon,
  CurrencyPoundIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  PlusIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { ukSchedulingAPI, timeAttendanceAPI, employeeAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend, PointElement, ArcElement } from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend, PointElement, ArcElement);

const ReportBuilder = () => {
  const [reports, setReports] = useState([]);
  const [reportTemplates, setReportTemplates] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportConfig, setReportConfig] = useState({
    name: '',
    description: '',
    type: 'attendance',
    chart_type: 'bar',
    filters: {
      date_range: 'last_30_days',
      custom_start_date: '',
      custom_end_date: '',
      departments: [],
      employees: [],
      positions: []
    },
    columns: [],
    grouping: 'daily',
    calculations: []
  });
  
  const [availableFilters, setAvailableFilters] = useState({
    departments: [],
    employees: [],
    positions: []
  });

  const { user, isAdmin, isManager } = useAuth();

  useEffect(() => {
    loadReports();
    loadReportTemplates();
    loadFilterOptions();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await ukSchedulingAPI.getReports();
      setReports(response.data.reports || []);
    } catch (error) {
      console.error('Error loading reports:', error);
      setError('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadReportTemplates = async () => {
    try {
      const response = await ukSchedulingAPI.getReportTemplates();
      setReportTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error loading report templates:', error);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const [deptResponse, empResponse, posResponse] = await Promise.all([
        employeeAPI.getDepartments(),
        employeeAPI.getAll({ active: true }),
        employeeAPI.getPositions()
      ]);

      setAvailableFilters({
        departments: deptResponse.data.departments || [],
        employees: empResponse.data.employees || [],
        positions: posResponse.data.positions || []
      });
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const generateReport = async (configOverride = null) => {
    try {
      setLoading(true);
      const config = configOverride || reportConfig;
      
      let response;
      switch (config.type) {
        case 'attendance':
          response = await timeAttendanceAPI.generateAttendanceReport(config);
          break;
        case 'scheduling':
          response = await ukSchedulingAPI.generateSchedulingReport(config);
          break;
        case 'payroll':
          response = await ukSchedulingAPI.generatePayrollReport(config);
          break;
        case 'compliance':
          response = await ukSchedulingAPI.generateComplianceReport(config);
          break;
        default:
          throw new Error('Invalid report type');
      }

      setReportData(response.data);
    } catch (error) {
      console.error('Error generating report:', error);
      setError('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const saveReport = async () => {
    try {
      setLoading(true);
      const reportToSave = {
        ...reportConfig,
        company_id: user.companyId,
        created_by: user.id,
        report_data: reportData
      };

      await ukSchedulingAPI.saveReport(reportToSave);
      await loadReports();
      setShowBuilder(false);
      resetReportConfig();
    } catch (error) {
      console.error('Error saving report:', error);
      setError('Failed to save report');
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;

    try {
      setLoading(true);
      await ukSchedulingAPI.deleteReport(reportId);
      await loadReports();
    } catch (error) {
      console.error('Error deleting report:', error);
      setError('Failed to delete report');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format, reportId = null) => {
    try {
      setLoading(true);
      const exportData = reportId ? 
        await ukSchedulingAPI.exportReport(reportId, format) :
        { data: reportData, config: reportConfig };

      // Create download link
      const blob = new Blob([
        format === 'csv' ? convertToCSV(exportData.data) : JSON.stringify(exportData.data, null, 2)
      ], { 
        type: format === 'csv' ? 'text/csv' : 'application/json'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `report_${Date.now()}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('Failed to export report');
    } finally {
      setLoading(false);
    }
  };

  const convertToCSV = (data) => {
    if (!Array.isArray(data) || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
    ].join('\n');
    
    return csvContent;
  };

  const resetReportConfig = () => {
    setReportConfig({
      name: '',
      description: '',
      type: 'attendance',
      chart_type: 'bar',
      filters: {
        date_range: 'last_30_days',
        custom_start_date: '',
        custom_end_date: '',
        departments: [],
        employees: [],
        positions: []
      },
      columns: [],
      grouping: 'daily',
      calculations: []
    });
    setReportData(null);
  };

  const handleFilterChange = (filterType, value) => {
    setReportConfig({
      ...reportConfig,
      filters: {
        ...reportConfig.filters,
        [filterType]: value
      }
    });
  };

  const addCalculation = () => {
    setReportConfig({
      ...reportConfig,
      calculations: [
        ...reportConfig.calculations,
        { field: '', operation: 'sum', alias: '' }
      ]
    });
  };

  const removeCalculation = (index) => {
    setReportConfig({
      ...reportConfig,
      calculations: reportConfig.calculations.filter((_, i) => i !== index)
    });
  };

  const getChartData = () => {
    if (!reportData || !reportData.chart_data) return null;

    const chartData = reportData.chart_data;
    
    return {
      labels: chartData.labels || [],
      datasets: chartData.datasets || [{
        label: 'Data',
        data: chartData.data || [],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)',
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(139, 92, 246, 1)',
        ],
        borderWidth: 1
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: reportConfig.name || 'Report Chart'
      },
    },
    scales: reportConfig.chart_type !== 'pie' ? {
      y: {
        beginAtZero: true
      }
    } : {}
  };

  const reportTypes = [
    { value: 'attendance', label: 'Attendance Report', icon: ClockIcon },
    { value: 'scheduling', label: 'Scheduling Report', icon: CalendarDaysIcon },
    { value: 'payroll', label: 'Payroll Report', icon: CurrencyPoundIcon },
    { value: 'compliance', label: 'Compliance Report', icon: DocumentTextIcon }
  ];

  const chartTypes = [
    { value: 'bar', label: 'Bar Chart' },
    { value: 'line', label: 'Line Chart' },
    { value: 'pie', label: 'Pie Chart' }
  ];

  const dateRanges = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const groupingOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'department', label: 'By Department' },
    { value: 'employee', label: 'By Employee' },
    { value: 'position', label: 'By Position' }
  ];

  const calculationOperations = [
    { value: 'sum', label: 'Sum' },
    { value: 'avg', label: 'Average' },
    { value: 'count', label: 'Count' },
    { value: 'min', label: 'Minimum' },
    { value: 'max', label: 'Maximum' }
  ];

  const renderChart = () => {
    const chartData = getChartData();
    if (!chartData) return null;

    switch (reportConfig.chart_type) {
      case 'line':
        return <Line data={chartData} options={chartOptions} />;
      case 'pie':
        return <Pie data={chartData} options={chartOptions} />;
      default:
        return <Bar data={chartData} options={chartOptions} />;
    }
  };

  const renderReportBuilder = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white min-h-[90vh]">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium text-gray-900">Report Builder</h3>
          <button
            onClick={() => {
              setShowBuilder(false);
              resetReportConfig();
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuration Panel */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Report Configuration</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report Name *
                  </label>
                  <input
                    type="text"
                    value={reportConfig.name}
                    onChange={(e) => setReportConfig({ ...reportConfig, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter report name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={reportConfig.description}
                    onChange={(e) => setReportConfig({ ...reportConfig, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Report description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Report Type *
                  </label>
                  <select
                    value={reportConfig.type}
                    onChange={(e) => setReportConfig({ ...reportConfig, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {reportTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chart Type
                  </label>
                  <select
                    value={reportConfig.chart_type}
                    onChange={(e) => setReportConfig({ ...reportConfig, chart_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {chartTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Range
                  </label>
                  <select
                    value={reportConfig.filters.date_range}
                    onChange={(e) => handleFilterChange('date_range', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {dateRanges.map(range => (
                      <option key={range.value} value={range.value}>
                        {range.label}
                      </option>
                    ))}
                  </select>
                </div>

                {reportConfig.filters.date_range === 'custom' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={reportConfig.filters.custom_start_date}
                        onChange={(e) => handleFilterChange('custom_start_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={reportConfig.filters.custom_end_date}
                        onChange={(e) => handleFilterChange('custom_end_date', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Group By
                  </label>
                  <select
                    value={reportConfig.grouping}
                    onChange={(e) => setReportConfig({ ...reportConfig, grouping: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {groupingOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Departments (Optional)
                  </label>
                  <select
                    multiple
                    value={reportConfig.filters.departments}
                    onChange={(e) => handleFilterChange('departments', Array.from(e.target.selectedOptions, option => option.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    size={4}
                  >
                    {availableFilters.departments.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Calculations */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-gray-900">Calculations</h4>
                <button
                  onClick={addCalculation}
                  className="text-primary-600 hover:text-primary-800"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>
              
              <div className="space-y-3">
                {reportConfig.calculations.map((calc, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <select
                      value={calc.operation}
                      onChange={(e) => {
                        const newCalcs = [...reportConfig.calculations];
                        newCalcs[index].operation = e.target.value;
                        setReportConfig({ ...reportConfig, calculations: newCalcs });
                      }}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                    >
                      {calculationOperations.map(op => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={calc.field}
                      onChange={(e) => {
                        const newCalcs = [...reportConfig.calculations];
                        newCalcs[index].field = e.target.value;
                        setReportConfig({ ...reportConfig, calculations: newCalcs });
                      }}
                      placeholder="Field"
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                    <button
                      onClick={() => removeCalculation(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => generateReport()}
                disabled={loading || !reportConfig.name}
                className="flex-1 btn-primary disabled:opacity-50 inline-flex items-center justify-center"
              >
                <PlayIcon className="h-4 w-4 mr-2" />
                {loading ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>

          {/* Report Preview */}
          <div className="lg:col-span-2">
            <div className="bg-white border rounded-lg p-6 min-h-[500px]">
              <div className="flex items-center justify-between mb-6">
                <h4 className="text-lg font-medium text-gray-900">Report Preview</h4>
                {reportData && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => exportReport('csv')}
                      className="text-sm text-primary-600 hover:text-primary-800"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 inline mr-1" />
                      CSV
                    </button>
                    <button
                      onClick={() => exportReport('json')}
                      className="text-sm text-primary-600 hover:text-primary-800"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4 inline mr-1" />
                      JSON
                    </button>
                    <button
                      onClick={saveReport}
                      disabled={loading}
                      className="btn-primary text-sm disabled:opacity-50"
                    >
                      Save Report
                    </button>
                  </div>
                )}
              </div>

              {loading && (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              )}

              {!loading && !reportData && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <ChartBarIcon className="h-12 w-12 mb-4" />
                  <p>Configure your report and click "Generate Report" to see the preview</p>
                </div>
              )}

              {reportData && (
                <div className="space-y-6">
                  {/* Chart */}
                  {reportData.chart_data && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h5 className="text-sm font-medium text-gray-900 mb-4">Chart Visualization</h5>
                      <div style={{ maxHeight: '400px' }}>
                        {renderChart()}
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  {reportData.summary && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h5 className="text-sm font-medium text-blue-900 mb-2">Report Summary</h5>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(reportData.summary).map(([key, value]) => (
                          <div key={key}>
                            <p className="text-xs text-blue-600 uppercase tracking-wide">{key.replace(/_/g, ' ')}</p>
                            <p className="text-lg font-semibold text-blue-900">{value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Data Table */}
                  {reportData.table_data && reportData.table_data.length > 0 && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h5 className="text-sm font-medium text-gray-900 mb-4">Data Table</h5>
                      <div className="overflow-x-auto max-h-64">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              {Object.keys(reportData.table_data[0]).map(header => (
                                <th key={header} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  {header.replace(/_/g, ' ')}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {reportData.table_data.slice(0, 10).map((row, index) => (
                              <tr key={index}>
                                {Object.values(row).map((cell, cellIndex) => (
                                  <td key={cellIndex} className="px-3 py-2 whitespace-nowrap text-gray-900">
                                    {cell}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {reportData.table_data.length > 10 && (
                          <div className="text-center py-2 text-sm text-gray-500">
                            ... and {reportData.table_data.length - 10} more rows
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Report Builder</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create custom reports and analytics for your organization
          </p>
        </div>
        {(isAdmin() || isManager()) && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => setShowBuilder(true)}
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Report
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XMarkIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Report Templates */}
      {reportTemplates.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Start Templates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {reportTemplates.map(template => {
              const reportType = reportTypes.find(t => t.value === template.type);
              const IconComponent = reportType?.icon || DocumentTextIcon;
              
              return (
                <div
                  key={template.id}
                  onClick={() => {
                    setReportConfig({ ...reportConfig, ...template.config });
                    setShowBuilder(true);
                  }}
                  className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 hover:shadow-sm cursor-pointer transition-all"
                >
                  <div className="flex items-center mb-2">
                    <IconComponent className="h-6 w-6 text-primary-600 mr-2" />
                    <h3 className="text-sm font-medium text-gray-900">{template.name}</h3>
                  </div>
                  <p className="text-xs text-gray-500">{template.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Saved Reports */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Saved Reports</h2>
        </div>
        
        {loading && reports.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-12">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No saved reports</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first custom report.
            </p>
            {(isAdmin() || isManager()) && (
              <div className="mt-6">
                <button
                  onClick={() => setShowBuilder(true)}
                  className="btn-primary inline-flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  New Report
                </button>
              </div>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {reports.map((report) => {
              const reportType = reportTypes.find(t => t.value === report.type);
              const IconComponent = reportType?.icon || DocumentTextIcon;
              
              return (
                <li key={report.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <IconComponent className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {report.name}
                          </p>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                            {report.type}
                          </span>
                        </div>
                        {report.description && (
                          <p className="mt-1 text-sm text-gray-500 truncate">
                            {report.description}
                          </p>
                        )}
                        <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                          <span>Created {new Date(report.created_at).toLocaleDateString()}</span>
                          {report.created_by_name && (
                            <span>by {report.created_by_name}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => generateReport(report.config)}
                        className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                      >
                        <EyeIcon className="h-4 w-4 inline mr-1" />
                        View
                      </button>
                      <button
                        onClick={() => exportReport('csv', report.id)}
                        className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 inline mr-1" />
                        Export
                      </button>
                      {(isAdmin() || report.created_by === user.id) && (
                        <button
                          onClick={() => deleteReport(report.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showBuilder && renderReportBuilder()}
    </div>
  );
};

export default ReportBuilder;